import { useEffect, useRef } from "react";
import { useParams } from "react-router";
import { Helmet } from "react-helmet";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { getSelectedSection } from "redux/Metadata/selectors";
import {
  fetchDataMetadataSummary,
  fetchMetadataSources,
} from "redux/SingleDataset/actions";
import vlIcon from "assets/img/newVLicon.svg";
import EnrichmentIcon from "assets/icons/EnrichmentIcon";
import EnrichmentModelsSection from "./Sections/EnrichmentModels";
import MetadataSection from "./Sections/Metadata";
import UserTagsSection from "./Sections/UserTags";
import QualitySection from "./Sections/Quality";
import LabelSection from "./Sections/Labels";

import {
  // getCaptions,
  getMetadataSummary,
  getDatasetEnrichmentModels,
  getDatasetExternalSources,
  getMetadataSummaryLoading,
  getIsMetadataSourcesLoading,
} from "redux/SingleDataset/selectors";
import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { useEntityType } from "hooks/useEntityType";

export interface Loaders {
  sources: boolean;
  metaData: boolean;
}

const MetadataSummary = () => {
  const { datasetId, clusterId } = useParams();
  const queryParams = useAllQueryParamsFilters();
  const dispatch = useDispatch();
  const entityType = useEntityType();
  const isDataPage = !!datasetId && !clusterId;

  // const captions = useSelector(getCaptions);
  const selectedSection = useSelector(getSelectedSection);
  const externalSources = useSelector(
    (state) => getDatasetExternalSources(state, queryParams),
    shallowEqual,
  );
  const enrichmentModels = useSelector(
    (state) => getDatasetEnrichmentModels(state, queryParams),
    shallowEqual,
  );
  const metadataSummary = useSelector(
    (state) => getMetadataSummary(state, queryParams, isDataPage),
    shallowEqual,
  );
  const isMetadataSummaryLoading = useSelector(
    (state) => getMetadataSummaryLoading(state, queryParams, isDataPage),
    shallowEqual,
  );
  const isMetadataSourcesLoading = useSelector(getIsMetadataSourcesLoading);

  const showVLEnrichment = !!enrichmentModels.length;

  useEffect(() => {
    if (!datasetId) return;
    dispatch(fetchDataMetadataSummary(datasetId, queryParams) as any);
    dispatch(fetchMetadataSources(datasetId, entityType) as any);
  }, [datasetId, queryParams, dispatch, entityType]);

  const parentContainerRef = useRef<HTMLDivElement>(null);
  const vlEnrichmentRef = useRef<HTMLDivElement>(null);
  const metadataRef = useRef<HTMLDivElement>(null);
  const userTagsRef = useRef<HTMLDivElement>(null);
  // const captionsRef = useRef<HTMLDivElement>(null);
  const qualityRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);

  const sectionHeight =
    !!parentContainerRef.current &&
    parentContainerRef.current.offsetHeight - 50;

  useEffect(() => {
    const parent = parentContainerRef.current;
    const vlEnrichment = vlEnrichmentRef.current;
    const metadata = metadataRef.current;
    const userTag = userTagsRef.current;
    const quality = qualityRef.current;
    // const caption = captionsRef.current;
    const labels = labelsRef.current;

    if (
      parent &&
      vlEnrichment &&
      metadata &&
      userTag &&
      quality &&
      // caption &&
      labels
    ) {
      switch (selectedSection) {
        case "vl-enrichment":
          labels.style.display = "none";
          // caption.style.display = "none";
          quality.style.display = "none";
          userTag.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.height = `${parent.clientHeight}px`;
          break;
        case "metadata":
          labels.style.display = "none";
          userTag.style.display = "none";
          quality.style.display = "none";
          // caption.style.display = "none";
          vlEnrichment.style.display = "none";
          metadata.style.height = `${parent.clientHeight}px`;
          break;
        case "user-tags":
          labels.style.display = "none";
          quality.style.display = "none";
          // caption.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          userTag.style.height = `${parent.clientHeight}px`;
          break;
        case "quality":
          labels.style.display = "none";
          userTag.style.display = "none";
          // caption.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          quality.style.height = `${parent.clientHeight}px`;
          break;
        case "captions":
          labels.style.display = "none";
          quality.style.display = "none";
          userTag.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          // caption.style.height = `${parent.clientHeight}px`;
          break;

        case "labels":
          quality.style.display = "none";
          userTag.style.display = "none";
          // caption.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          labels.style.height = `${parent.clientHeight}px`;
          break;

        case null:
          vlEnrichment.style.display = "unset";
          metadata.style.display = "unset";
          userTag.style.display = "unset";
          quality.style.display = "unset";
          // caption.style.display = "unset";
          labels.style.display = "unset";

          vlEnrichment.style.height = "max-content";
          metadata.style.height = "max-content";
          userTag.style.height = "max-content";
          quality.style.height = "max-content";
          // caption.style.height = "max-content";
          labels.style.height = "max-content";
          break;

        default:
          return;
      }
    }
  }, [selectedSection]);

  return (
    <div ref={parentContainerRef} className={styles.metaDataSummaryWrapper}>
      <Helmet>
        <link rel="preload" href={vlIcon} as="image" type="image/svg+xml" />
      </Helmet>
      <div className={styles.expandedContainer}>
        <EnrichmentModelsSection
          isLoading={isMetadataSourcesLoading}
          models={enrichmentModels}
          sources={externalSources}
          vlEnrichmentRef={vlEnrichmentRef}
          showVLEnrichment={showVLEnrichment}
          icon={<EnrichmentIcon />}
          sectionHeight={selectedSection === "vl-enrichment" && sectionHeight}
        />

        <QualitySection
          isLoading={isMetadataSummaryLoading}
          qualityRef={qualityRef}
          issueTypes={metadataSummary!.issueTypes}
          sectionHeight={selectedSection === "quality" && sectionHeight}
        />

        <LabelSection
          labels={metadataSummary!.mediaLabels}
          labelsRef={labelsRef}
          sectionHeight={selectedSection === "labels" && sectionHeight}
          isLoading={isMetadataSummaryLoading}
        />

        {/* <CaptionSection
          captions={captions}
          isLoading={isLoading}
          captionsRef={captionsRef}
          sectionSearchIcon={renderSectionSearchIcon(
            "captions",
            captions.length
          )}
          sectionHeight={selectedSection === "captions" && sectionHeight}
        /> */}

        <UserTagsSection
          isLoading={isMetadataSummaryLoading}
          userTagsRef={userTagsRef}
          userTags={metadataSummary!.userTags}
          sectionHeight={selectedSection === "user-tags" && sectionHeight}
        />

        <MetadataSection
          isLoading={isMetadataSourcesLoading}
          metadataRef={metadataRef}
          sources={externalSources}
          models={enrichmentModels}
          videoData={metadataSummary?.videoData}
          sectionHeight={selectedSection === "metadata" && sectionHeight}
        />
      </div>
    </div>
  );
};

export default MetadataSummary;
